import { createCanvas } from './loadImage';
import type { CustomPlacement } from '../types';
import {
  buildMeshPointsShader,
  toShaderPoint,
  toShaderPoints,
  type MeshPoint
} from './meshGradientPoints';
import { normalizeStopPositions, type AxisPoint } from './gradientAxis';

export type StaticBackgroundMode = 'none' | 'solid' | 'linear' | 'radial' | 'mesh' | 'image';
export type StaticBackgroundImageFit = 'cover' | 'contain';

export interface StaticBackgroundComposition {
  mode: StaticBackgroundMode;
  colors: string[];
  /** Linear: the two ends of the ramp. Radial: centre, then a point on the rim. */
  axisStart: AxisPoint;
  axisEnd: AxisPoint;
  /** One per colour, 0 at the start handle and 1 at the end one. */
  stopPositions: number[];
  angle: number;
  meshPoints: MeshPoint[];
  meshMix: number;
  meshDistortion: number;
  meshGrain: number;
  /** Where the Flow distortion is strongest; it fades out from here. */
  meshFlowCenter: MeshPoint;
  image: CanvasImageSource | null;
  imageFit: StaticBackgroundImageFit;
  imagePlacement: CustomPlacement | null;
}

export function isStaticBackgroundActive(composition: StaticBackgroundComposition | null | undefined): boolean {
  return Boolean(composition && composition.mode !== 'none' && (composition.mode !== 'image' || composition.image));
}

export async function renderStaticBackground(
  composition: StaticBackgroundComposition,
  size: number
): Promise<HTMLCanvasElement | null> {
  if (!isStaticBackgroundActive(composition)) {
    return null;
  }

  const normalizedSize = Math.max(1, Math.round(size));
  if (composition.mode === 'mesh') {
    return renderPaperMeshGradient(composition, normalizedSize);
  }

  const canvas = createCanvas(normalizedSize, normalizedSize);
  drawStaticCanvasBackground(composition, canvas);
  return canvas;
}

function drawStaticCanvasBackground(
  composition: StaticBackgroundComposition,
  canvas: HTMLCanvasElement
): void {
  const normalizedSize = canvas.width;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Static background canvas is unavailable.');
  }
  ctx.clearRect(0, 0, normalizedSize, normalizedSize);

  if (composition.mode === 'solid') {
    ctx.fillStyle = composition.colors[0];
    ctx.fillRect(0, 0, normalizedSize, normalizedSize);
    return;
  }

  if (composition.mode === 'linear') {
    const gradient = ctx.createLinearGradient(
      composition.axisStart.x * normalizedSize,
      composition.axisStart.y * normalizedSize,
      composition.axisEnd.x * normalizedSize,
      composition.axisEnd.y * normalizedSize
    );
    addStops(gradient, composition.colors, composition.stopPositions);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, normalizedSize, normalizedSize);
    return;
  }

  if (composition.mode === 'radial') {
    // First handle is the centre, second sets how far the ramp reaches.
    const x = composition.axisStart.x * normalizedSize;
    const y = composition.axisStart.y * normalizedSize;
    const radius = Math.max(
      1,
      Math.hypot(
        (composition.axisEnd.x - composition.axisStart.x) * normalizedSize,
        (composition.axisEnd.y - composition.axisStart.y) * normalizedSize
      )
    );
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    addStops(gradient, composition.colors, composition.stopPositions);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, normalizedSize, normalizedSize);
    return;
  }

  if (composition.mode === 'image' && composition.image) {
    drawFittedImage(
      ctx,
      composition.image,
      normalizedSize,
      composition.imageFit,
      composition.imagePlacement
    );
    return;
  }
}

/** Lays the colours onto the ramp at the positions the author dragged them to. */
function addStops(gradient: CanvasGradient, colors: string[], positions: number[]): void {
  const usable = colors.filter((color) => typeof color === 'string' && color.length > 0);
  if (usable.length === 0) {
    return;
  }
  if (usable.length === 1) {
    gradient.addColorStop(0, usable[0]);
    gradient.addColorStop(1, usable[0]);
    return;
  }

  const offsets = normalizeStopPositions(positions, usable.length);
  usable.forEach((color, index) => {
    gradient.addColorStop(offsets[index], color);
  });
}

function drawFittedImage(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  size: number,
  fit: StaticBackgroundImageFit,
  placement: CustomPlacement | null
): void {
  const sourceWidth = getSourceWidth(image);
  const sourceHeight = getSourceHeight(image);
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    return;
  }

  const baseScale = placement
    ? Math.max(size / sourceWidth, size / sourceHeight)
    : fit === 'contain'
      ? Math.min(size / sourceWidth, size / sourceHeight)
      : Math.max(size / sourceWidth, size / sourceHeight);
  const customScale = placement ? clamp(placement.scale, 0.05, 6) : 1;
  const scale = baseScale * customScale;
  const width = sourceWidth * scale;
  const height = sourceHeight * scale;
  const offsetX = placement && Number.isFinite(placement.offsetX) ? placement.offsetX : 0;
  const offsetY = placement && Number.isFinite(placement.offsetY) ? placement.offsetY : 0;
  const x = (size - width) / 2 + offsetX * size;
  const y = (size - height) / 2 + offsetY * size;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  if (!placement && fit === 'contain') {
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, size, size);
  }
  ctx.drawImage(image, x, y, width, height);
}

/** Where the Grain slider's 100% lands on the shader's own scale. */
const MESH_GRAIN_CEILING = 0.3;

/** The uniforms that change as the author works, shared by the one-shot render
 *  and the live editor preview so the two can never drift apart. */
function buildMeshUniforms(
  composition: StaticBackgroundComposition,
  pointCount: number,
  toShaderColor: (color: string) => number[]
): Record<string, number | number[] | number[][]> {
  const colors = composition.colors.map((color) => toShaderColor(color));
  // The shader's grain is far coarser than a mockup background wants: past
  // roughly a third of its range it stops reading as texture and starts reading
  // as noise. The slider therefore spends its whole travel inside that third.
  const grain = (clamp(composition.meshGrain, 0, 100) / 100) * MESH_GRAIN_CEILING;
  return {
    u_colors: colors,
    u_colorsCount: colors.length,
    // Replaces the stock seeded arrangement — see meshGradientPoints.ts.
    u_points: toShaderPoints(composition.meshPoints, pointCount),
    u_flowCenter: toShaderPoint(composition.meshFlowCenter),
    u_waveX: clamp(composition.meshDistortion, 0, 100) / 100,
    u_waveXShift: 0.16,
    u_waveY: clamp(composition.meshDistortion * 0.82, 0, 100) / 100,
    u_waveYShift: 0.64,
    u_mixing: clamp(composition.meshMix, 0, 100) / 100,
    // Grain softens the blend edges and breaks up flat areas; the overlay is
    // kept lighter than the mixer so it reads as texture, not noise.
    u_grainMixer: grain,
    u_grainOverlay: grain * 0.6
  };
}

/**
 * A mesh preview that stays mounted.
 *
 * Rendering a mesh frame normally costs a WebGL mount, two paints and a
 * teardown, which is far too much to repeat while a point is being dragged —
 * the dialog visibly stalls and flickers. Holding one mount open instead makes
 * an update a uniform upload and a synchronous draw, so the gradient tracks the
 * pointer. The export path is untouched and still renders its own frame.
 */
export interface MeshPreviewSession {
  /** False when the mount never came up, so the caller can retry rather than
   *  publish an empty frame. */
  draw(composition: StaticBackgroundComposition, target: HTMLCanvasElement): boolean;
  dispose(): void;
}

/**
 * A reusable renderer for a video timeline.
 *
 * Animated backgrounds redraw hundreds of frames. Reusing one canvas avoids a
 * large allocation per frame, while Mesh additionally keeps one WebGL mount
 * alive for the export instead of compiling and tearing down the shader for
 * every frame.
 */
export interface StaticBackgroundSequenceRenderer {
  render(composition: StaticBackgroundComposition): Promise<HTMLCanvasElement>;
  dispose(): void;
}

export async function createStaticBackgroundSequenceRenderer(
  initial: StaticBackgroundComposition,
  size: number
): Promise<StaticBackgroundSequenceRenderer> {
  const normalizedSize = Math.max(1, Math.round(size));
  const output = createCanvas(normalizedSize, normalizedSize);

  if (initial.mode !== 'mesh') {
    return {
      async render(composition) {
        if (composition.mode === 'mesh') {
          throw new Error('The animated background mode changed during export.');
        }
        drawStaticCanvasBackground(composition, output);
        return output;
      },
      dispose() {
        output.width = 1;
        output.height = 1;
      }
    };
  }

  const session = await createMeshPreviewSession(normalizedSize, initial);
  if (!session || !session.draw(initial, output)) {
    session?.dispose();
    throw new Error('Mesh gradient could not be initialized for video export.');
  }

  return {
    async render(composition) {
      if (composition.mode !== 'mesh') {
        throw new Error('The animated background mode changed during export.');
      }
      if (!session.draw(composition, output)) {
        throw new Error('Mesh gradient could not be rendered.');
      }
      return output;
    },
    dispose() {
      session.dispose();
      output.width = 1;
      output.height = 1;
    }
  };
}

export async function createMeshPreviewSession(
  size: number,
  // ShaderMount only looks up uniform locations for what it is handed at
  // construction; anything introduced later is silently dropped, which renders
  // black. The first composition therefore has to be complete.
  initial: StaticBackgroundComposition
): Promise<MeshPreviewSession | null> {
  const {
    ShaderFitOptions,
    ShaderMount,
    defaultPatternSizing,
    getShaderColorFromString,
    staticMeshGradientFragmentShader,
    staticMeshGradientMeta
  } = await import('@paper-design/shaders');

  const pointCount = staticMeshGradientMeta.maxColorCount;
  const host = createOffscreenHost(size);
  document.body.appendChild(host);

  const mount = new ShaderMount(
    host,
    buildMeshPointsShader(staticMeshGradientFragmentShader, pointCount),
    {
      u_fit: ShaderFitOptions.none,
      u_scale: defaultPatternSizing.scale,
      u_rotation: defaultPatternSizing.rotation,
      u_originX: defaultPatternSizing.originX,
      u_originY: defaultPatternSizing.originY,
      u_offsetX: defaultPatternSizing.offsetX,
      u_offsetY: defaultPatternSizing.offsetY,
      u_worldWidth: defaultPatternSizing.worldWidth,
      u_worldHeight: defaultPatternSizing.worldHeight,
      ...buildMeshUniforms(initial, pointCount, getShaderColorFromString)
    },
    { alpha: false, antialias: true, preserveDrawingBuffer: true },
    0,
    0,
    1,
    size * size
  );

  // One wait, at creation, for the mount to size itself. Every later frame is
  // synchronous.
  await waitForPaint();

  let disposed = false;
  return {
    draw(composition, target) {
      if (disposed || mount.canvasElement.width <= 0 || mount.canvasElement.height <= 0) {
        return false;
      }
      mount.setUniforms(buildMeshUniforms(composition, pointCount, getShaderColorFromString));
      mount.setFrame(0);
      const ctx = target.getContext('2d');
      if (!ctx) {
        return false;
      }
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(mount.canvasElement, 0, 0, target.width, target.height);
      return true;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      mount.dispose();
      host.remove();
    }
  };
}

function createOffscreenHost(size: number): HTMLDivElement {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  Object.assign(host.style, {
    position: 'fixed',
    inset: '0 auto auto 0',
    width: `${size}px`,
    height: `${size}px`,
    opacity: '0',
    pointerEvents: 'none',
    zIndex: '-1',
    contain: 'strict'
  });
  return host;
}

/**
 * Preview-sized mesh renders reuse one mount.
 *
 * Building and tearing down a WebGL context per frame is what made the panel
 * stall: every slider nudge and colour pick paid for a mount, two paints and a
 * teardown. Interactive sizes therefore keep a mount alive between renders and
 * only upload uniforms, while an export — which happens once and would pin a
 * very large buffer — still gets its own short-lived mount.
 */
const MESH_SESSION_MAX_SIZE = 1200;
/**
 * One size for every interactive render, whatever size was asked for.
 *
 * The preview asks for different sizes depending on what the user is doing —
 * it drops to 384 while a slider is being dragged and returns to the template's
 * edit size after. Keying the mount on that size meant rebuilding the WebGL
 * context at the start of every drag and again at the end, roughly a second
 * each time, which read as the preview freezing and vanishing exactly while a
 * slider was moving. A gradient carries no detail that needs native
 * resolution, so one mount serves them all and the result is scaled on the way
 * out.
 */
const MESH_SESSION_SIZE = 1024;
let sharedMeshSession: Promise<MeshPreviewSession | null> | null = null;

function releaseSharedMeshSession(): void {
  const current = sharedMeshSession;
  sharedMeshSession = null;
  void current?.then((session) => session?.dispose()).catch(() => undefined);
}

async function renderPaperMeshGradient(
  composition: StaticBackgroundComposition,
  size: number
): Promise<HTMLCanvasElement> {
  const output = createCanvas(size, size);

  if (size <= MESH_SESSION_MAX_SIZE) {
    if (!sharedMeshSession) {
      sharedMeshSession = createMeshPreviewSession(MESH_SESSION_SIZE, composition);
    }
    const session = await sharedMeshSession;
    if (session?.draw(composition, output)) {
      return output;
    }
    // The mount never came up; fall through and try a fresh one rather than
    // handing back an empty canvas.
    releaseSharedMeshSession();
  }

  // Export resolution gets its own short-lived mount: it happens once, and
  // holding a buffer that large open for the session would not pay for itself.
  const oneShot = await createMeshPreviewSession(size, composition);
  try {
    if (!oneShot?.draw(composition, output)) {
      throw new Error('Mesh gradient could not be rendered.');
    }
    return output;
  } finally {
    oneShot?.dispose();
  }
}

/**
 * Two paints, or a timeout.
 *
 * The frames are what let the shader mount size itself before the frame is
 * grabbed. A backgrounded tab stops serving them entirely, so waiting on
 * requestAnimationFrame alone means an export started and then left in another
 * tab never finishes. Falling through after a beat keeps that export moving;
 * the canvas-size check downstream still rejects a mount that never came up.
 */
const PAINT_TIMEOUT_MS = 400;

function waitForPaint(): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(finish, PAINT_TIMEOUT_MS);
    window.requestAnimationFrame(() => window.requestAnimationFrame(finish));
  });
}

function getSourceWidth(source: CanvasImageSource): number {
  return 'videoWidth' in source && Number(source.videoWidth) > 0
    ? Number(source.videoWidth)
    : 'naturalWidth' in source && Number(source.naturalWidth) > 0
      ? Number(source.naturalWidth)
      : 'width' in source
        ? Number(source.width)
        : 0;
}

function getSourceHeight(source: CanvasImageSource): number {
  return 'videoHeight' in source && Number(source.videoHeight) > 0
    ? Number(source.videoHeight)
    : 'naturalHeight' in source && Number(source.naturalHeight) > 0
      ? Number(source.naturalHeight)
      : 'height' in source
        ? Number(source.height)
        : 0;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
